import React, { useState } from 'react';
import { Sparkles, Info, X, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContribBar = ({ contribution, favors }) => {
  const abs = Math.min(Math.abs(contribution), 10);
  const pct = (abs / 10) * 100;
  const color = favors === 'home' ? 'bg-blue-400' : 'bg-amber-400';
  return (
    <div className="w-full h-1.5 bg-slate-700/40 rounded-full overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

export const ConfidenceBreakdownButton = ({ gameId, leagueId, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if ((leagueId || '').toLowerCase() !== 'mlb') return null;

  const fetchData = async () => {
    if (data) return;  // already fetched once; respect endpoint-level TTL on subsequent opens
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/games/${gameId}/confidence-breakdown`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      setData(json);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const onOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
    setError(null);  // clear stale error so user can retry after a failed fetch
    fetchData();
  };

  const onClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className={`inline-flex items-center gap-1 ${
          compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
        } rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 transition-colors`}
        data-testid={`confidence-breakdown-trigger-${gameId}`}
        aria-label="Why this prediction?"
      >
        <Sparkles className="w-3 h-3" />
        {compact ? 'Why?' : 'Why this prediction?'}
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
          data-testid={`confidence-breakdown-modal-${gameId}`}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
              <h3 className="text-sm font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                <Info className="w-4 h-4 text-amber-400" />
                Confidence Breakdown
              </h3>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground p-1"
                aria-label="Close"
                data-testid={`confidence-breakdown-close-${gameId}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {!loading && error && (
                <p className="text-sm text-rose-400 py-4">{error}</p>
              )}

              {!loading && data && !data.available && (
                <p className="text-sm text-muted-foreground py-4">
                  {data.message || 'Prediction factors not yet computed for this game.'}
                </p>
              )}

              {!loading && data?.available && (
                <div className="space-y-4">
                  <p
                    className="text-sm leading-relaxed text-foreground"
                    data-testid={`confidence-breakdown-summary-${gameId}`}
                  >
                    {data.summary}
                  </p>

                  <div className="space-y-3">
                    {(data.factors || []).map((f, idx) => (
                      <div key={f.label} className="space-y-1" data-testid={`confidence-factor-${idx}`}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-foreground flex items-center gap-1.5">
                            {f.label}
                            {idx === 0 && Math.abs(f.contribution) >= 1 && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase tracking-wider">
                                Top
                              </span>
                            )}
                          </span>
                          <span
                            className={`font-mono font-semibold flex items-center gap-1 ${
                              f.favors === 'home' ? 'text-blue-300' : 'text-amber-300'
                            }`}
                          >
                            {f.favors === 'home' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {f.contribution > 0 ? '+' : ''}{f.contribution} → {f.favors_team}
                          </span>
                        </div>
                        <ContribBar contribution={f.contribution} favors={f.favors} />
                        <p className="text-[11px] text-muted-foreground">{f.detail}</p>
                      </div>
                    ))}
                  </div>

                  <p className="text-[10px] text-muted-foreground italic pt-2 border-t border-slate-800">
                    Contributions are estimated probability swings (in percentage points). MLB 11-Principle System.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConfidenceBreakdownButton;
